import React, { useState } from 'react'
import loginBackground from '@/assets/images/login_background.png'
import valiantlytixLogo from '@/assets/images/darkLogo.png'
import { Button, Form, Input, Checkbox, Modal } from 'antd'
import { LockFilled, UserOutlined, EyeInvisibleOutlined, EyeTwoTone, KeyOutlined, BankOutlined, IdcardOutlined, MailOutlined, LoginOutlined } from '@ant-design/icons'
import { MdOutlineVpnKey } from 'react-icons/md'
import { Link, useNavigate, useSearchParams } from 'react-router'
import logo from '@/assets/images/vantageAI.png'
import axios from 'axios';
import { toast } from 'sonner'
import { useNotificationStore } from '@/store/notificationStore'
import './NewLoginPage.css';
import { useUserActions } from '@/store/userStore'
import AuthErrorModal from '@/components/AuthErrorModal';

const ClentPrefilledSignUpPage = () => {

  const navigate = useNavigate();
  const { VITE_APP_URL: URL } = import.meta.env;
  const { VITE_AGENT_URL: AGENT_URL } = import.meta.env;

  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email");
  const companyParam = searchParams.get("company");
  const tokenParam = searchParams.get("token") || "";

  const [name, setName] = useState("");
  const [email,setEmail] = useState(emailParam || "");
  const [password,setPassword] = useState("");
  const [companyName,setCompanyName] = useState(companyParam || "");
  const [designation,setDesignation] = useState("");

  const { setUserInfo, setCreditsRemaining, setTotalCreditsAllocated, setDaysRemaining, setTier, setTierId, setFeatureAccess } = useUserActions()

  const [buttonLoading,setButtonLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string>("Registration Error");
  const [fieldErrors, setFieldErrors] = useState<{
    name?: boolean;
    companyName?: boolean;
    designation?: boolean;
    email?: boolean;
    password?: boolean;
    termsAccepted?: boolean;
  }>({});

  const handleCreateAccount = async() =>{
    try{
      const missing: string[] = [];
      const newErrors: typeof fieldErrors = {};

      if (!name.trim()) {
        missing.push("Name");
        newErrors.name = true;
      }
      if (!companyName.trim()) {
        missing.push("Company");
        newErrors.companyName = true;
      }
      if (!designation.trim()) {
        missing.push("Designation");
        newErrors.designation = true;
      }
      if (!email.trim()) {
        missing.push("Email");
        newErrors.email = true;
      }
      if (!password) {
        missing.push("Password");
        newErrors.password = true;
      }

      if (missing.length > 0) {
        setFieldErrors(newErrors);
        setErrorTitle("Missing Required Fields");
        setErrorMessage(`Please fill in the missing field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}.`);
        return;
      }

      if (!termsAccepted) {
        setFieldErrors({ termsAccepted: true });
        setErrorTitle("Privacy Policy Required");
        setErrorMessage("Please accept the Privacy Policy to proceed.");
        return;
      }

      setButtonLoading(true);
      const response = await axios.post(`${URL}/api/enterprise/onboarding/admin-signup/${tokenParam}`,{
        name,email,password, "company_name":companyName, "designation":designation
      })

      if (response.data && response.data.status === false) {
        setErrorTitle("Registration Failed");
        setErrorMessage(response.data.message || "Email already exists");
        setButtonLoading(false);
        return;
      }

    // Dispatch signup notification
    const { addNotification } = useNotificationStore.getState().actions;
    addNotification({
      type: 'system',
      title: 'Account Created',
      message: `Welcome, ${name || email}! Your enterprise account has been created successfully.`,
    });

    setButtonLoading(false);	
      // Auto-login so the user isn't prompted to login after returning from Stripe
      try {
        const loginResponse = await axios.post(`${URL}/api/users/login`, {
          email,
          password,
        });

        const userId = loginResponse.data?.user_info?.user_id || loginResponse.data?.user_info?.id;

        localStorage.removeItem("pending_payment");
        sessionStorage.removeItem("active_transaction_id");

        localStorage.setItem("roleId", loginResponse.data?.role_id);
        localStorage.setItem("userId", userId);
        localStorage.setItem("departmentName", loginResponse.data?.user_info?.department);

        setCreditsRemaining(null);
        setTotalCreditsAllocated(null);
        setDaysRemaining(null);
        setFeatureAccess({});
        setTier(loginResponse.data?.user_info?.tier || null);
        setTierId(loginResponse.data?.user_info?.tier_id || null);
        setUserInfo(loginResponse.data?.user_info);
      } catch (loginErr) {
        console.error("Auto login failed", loginErr);
      }
      
      toast.success("Account created successfully. Please proceed to payment.")
      navigate("/enterprise-payment-prompt", { state: { userId: response.data.user_id } })
      

    }catch(err: any){
      console.log(err)
      setErrorTitle("Registration Failed");
      if (err.response && err.response.data && (err.response.data.message || err.response.data.error)) {
        setErrorMessage(err.response.data.message || err.response.data.error);
      } else {
        setErrorMessage("An error occurred while creating your account. Please try again.");
      }
    } finally {
            setButtonLoading(false);
        }
  }

  return (
    <div className='flex w-full h-screen bg-[#F8F9FA] font-sans'>
      {/* Left side: Form Panel */}
      <div className='w-full lg:w-1/2 h-full flex flex-col items-center relative px-4 sm:px-8 overflow-y-auto'>
        
        <div className='w-full max-w-[440px] flex flex-col items-center my-auto py-8'>
          {/* Logo at Top */}
          <div className='w-full flex justify-center mb-8'>
            <img src={logo} alt="Vantage AI Logo" className='w-[160px] md:w-[200px] h-auto object-contain' />
          </div>

          {/* Form Container */}
          <div className='w-full bg-white px-6 sm:px-8 py-8 sm:py-10 rounded-2xl shadow-sm border border-gray-100 flex flex-col'>
            <div className='flex flex-col gap-2 mb-6'>
              <div className='flex items-center gap-3'>
                <svg width="13" height="18" viewBox="0 0 13 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.56888 17.85C1.13546 17.85 0.765625 17.6958 0.459375 17.3873C0.153125 17.0789 0 16.7081 0 16.275V7.875C0 7.44187 0.154219 7.07109 0.462656 6.76266C0.771094 6.45422 1.14188 6.3 1.575 6.3H2.1V4.2C2.1 3.038 2.51001 2.0475 3.33003 1.2285C4.14991 0.409499 5.14157 0 6.30503 0C7.46834 0 8.45833 0.409499 9.275 1.2285C10.0917 2.0475 10.5 3.038 10.5 4.2V6.3H11.025C11.4581 6.3 11.8289 6.45422 12.1373 6.76266C12.4458 7.07109 12.6 7.44187 12.6 7.875V16.275C12.6 16.7081 12.4457 17.0789 12.1371 17.3873C11.8285 17.6958 11.4575 17.85 11.0241 17.85H1.56888ZM1.575 16.275H11.025V7.875H1.575V16.275ZM6.30459 13.65C6.73903 13.65 7.10938 13.4953 7.41563 13.186C7.72188 12.8767 7.875 12.5048 7.875 12.0704C7.875 11.636 7.72034 11.2656 7.41103 10.9594C7.10172 10.6531 6.72984 10.5 6.29541 10.5C5.86097 10.5 5.49063 10.6547 5.18438 10.964C4.87813 11.2733 4.725 11.6452 4.725 12.0796C4.725 12.514 4.87966 12.8844 5.18897 13.1906C5.49828 13.4969 5.87016 13.65 6.30459 13.65ZM3.675 6.3H8.925V4.2C8.925 3.47083 8.66979 2.85104 8.15938 2.34063C7.64896 1.83021 7.02917 1.575 6.3 1.575C5.57083 1.575 4.95104 1.83021 4.44063 2.34063C3.93021 2.85104 3.675 3.47083 3.675 4.2V6.3Z" fill="#A51C1C"/>
</svg>

                <span className='text-xl md:text-2xl font-semibold tracking-[0.2px] text-gray-900'>Create an account</span>
              </div>
              <span className='text-gray-500 text-sm'>Please fill the below details to get started</span>
            </div>

            <div className='flex flex-col gap-y-3'>
              <Form.Item label={<span className='text-sm font-medium text-gray-700 mb-0 block'>Name</span>} className='mb-0' layout='vertical'>
                <Input
                  size="large"
                  status={fieldErrors.name ? 'error' : undefined}
                  style={{ backgroundColor: "#F8F9FA", borderColor: fieldErrors.name ? "#EF4444" : "#E5E7EB", height: 42 }}
                  placeholder='John'
                  prefix={<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5.7 5.7C4.90833 5.7 4.23542 5.42292 3.68125 4.86875C3.12708 4.31458 2.85 3.64167 2.85 2.85C2.85 2.05833 3.12708 1.38542 3.68125 0.83125C4.23542 0.277083 4.90833 0 5.7 0C6.49167 0 7.16458 0.277083 7.71875 0.83125C8.27292 1.38542 8.55 2.05833 8.55 2.85C8.55 3.64167 8.27292 4.31458 7.71875 4.86875C7.16458 5.42292 6.49167 5.7 5.7 5.7ZM0 11.4V9.5C0 9.19653 0.0824653 8.90955 0.247396 8.63906C0.412326 8.36858 0.639931 8.14097 0.930208 7.95625C1.6559 7.53403 2.42309 7.21076 3.23178 6.98646C4.04047 6.76215 4.86182 6.65 5.69584 6.65C6.52986 6.65 7.3526 6.76215 8.16406 6.98646C8.97552 7.21076 9.7441 7.53403 10.4698 7.95625C10.7601 8.12778 10.9877 8.35208 11.1526 8.62917C11.3175 8.90625 11.4 9.19653 11.4 9.5V11.4H0ZM1.425 9.975H9.975V9.5C9.975 9.43165 9.95501 9.36957 9.91503 9.31376C9.87518 9.25782 9.8226 9.21434 9.75729 9.18333C9.15035 8.81389 8.50382 8.53681 7.81771 8.35208C7.1316 8.16736 6.42569 8.075 5.7 8.075C4.97431 8.075 4.2684 8.16736 3.58229 8.35208C2.89618 8.53681 2.24965 8.81389 1.64271 9.18333C1.57674 9.23611 1.52396 9.28704 1.48438 9.33613C1.44479 9.38534 1.425 9.43997 1.425 9.5V9.975ZM5.70416 4.275C6.09722 4.275 6.43229 4.13507 6.70937 3.85522C6.98646 3.57536 7.125 3.23891 7.125 2.84584C7.125 2.45278 6.98507 2.11771 6.70522 1.84062C6.42536 1.56354 6.08891 1.425 5.69584 1.425C5.30278 1.425 4.96771 1.56493 4.69062 1.84478C4.41354 2.12464 4.275 2.46109 4.275 2.85416C4.275 3.24722 4.41493 3.58229 4.69478 3.85938C4.97464 4.13646 5.31109 4.275 5.70416 4.275Z" fill="#9AA1AD"/>
</svg>
}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: false }));
                  }}
                  className={`light-input rounded-lg hover:border-gray-300 focus:border-[#A91D22] ${fieldErrors.name ? '!border-red-500' : ''}`}
                />
              </Form.Item>

              <Form.Item label={<span className='text-sm font-medium text-gray-700 mb-0 block'>Company</span>} className='mb-0' layout='vertical'>
                <Input
                  disabled
                  size="large"
                  status={fieldErrors.companyName ? 'error' : undefined}
                  style={{ backgroundColor: "#F8F9FA", borderColor: fieldErrors.companyName ? "#EF4444" : "#E5E7EB", height: 42 }}
                  placeholder='Valiantlytix'
                  prefix={<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0 13.3V2.85H2.85V0H9.975V5.7H13.3V13.3H7.6V10.45H5.7V13.3H0ZM1.425 11.875H2.85V10.45H1.425V11.875ZM1.425 8.7875H2.85V7.3625H1.425V8.7875ZM1.425 5.7H2.85V4.275H1.425V5.7ZM4.275 8.7875H5.7V7.3625H4.275V8.7875ZM4.275 5.7H5.7V4.275H4.275V5.7ZM4.275 2.85H5.7V1.425H4.275V2.85ZM7.125 8.7875H8.55V7.3625H7.125V8.7875ZM7.125 5.7H8.55V4.275H7.125V5.7ZM7.125 2.85H8.55V1.425H7.125V2.85ZM10.45 11.875H11.875V10.45H10.45V11.875ZM10.45 8.7875H11.875V7.3625H10.45V8.7875Z" fill="#9AA1AD"/>
</svg>
}
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    if (fieldErrors.companyName) setFieldErrors(prev => ({ ...prev, companyName: false }));
                  }}
                  className={`light-input rounded-lg hover:border-gray-300 focus:border-[#A91D22] ${fieldErrors.companyName ? '!border-red-500' : ''}`}
                />
              </Form.Item>

              <Form.Item label={<span className='text-sm font-medium text-gray-700 mb-0 block'>Designation</span>} className='mb-0' layout='vertical'>
                <Input
                  autoComplete="off"
                  name="designation"
                  size="large"
                  status={fieldErrors.designation ? 'error' : undefined}
                  style={{ backgroundColor: "#F8F9FA", borderColor: fieldErrors.designation ? "#EF4444" : "#E5E7EB", height: 42 }}
                  placeholder='Developer'
                  prefix={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.425 15.2C1.03313 15.2 0.697656 15.0605 0.418594 14.7814C0.139531 14.5023 0 14.1669 0 13.775V5.225C0 4.83313 0.139531 4.49766 0.418594 4.21859C0.697656 3.93953 1.03313 3.8 1.425 3.8H5.7V1.425C5.7 1.03313 5.83953 0.697657 6.11859 0.418594C6.39766 0.139532 6.73312 0 7.125 0H8.075C8.46688 0 8.80234 0.139532 9.08141 0.418594C9.36047 0.697657 9.5 1.03313 9.5 1.425V3.8H13.775C14.1669 3.8 14.5023 3.93953 14.7814 4.21859C15.0605 4.49766 15.2 4.83313 15.2 5.225V13.775C15.2 14.1669 15.0605 14.5023 14.7814 14.7814C14.5023 15.0605 14.1669 15.2 13.775 15.2H1.425ZM1.425 13.775H13.775V5.225H9.5C9.5 5.62083 9.36047 5.95729 9.08141 6.23438C8.80234 6.51146 8.46688 6.65 8.075 6.65H7.125C6.73312 6.65 6.39766 6.51047 6.11859 6.23141C5.83953 5.95234 5.7 5.61687 5.7 5.225H1.425V13.775ZM2.85 12.35H7.6V11.8948C7.6 11.6622 7.53733 11.4466 7.41198 11.2482C7.28663 11.0499 7.11181 10.896 6.8875 10.7865C6.62361 10.6809 6.35642 10.5984 6.08594 10.5391C5.81545 10.4797 5.52847 10.45 5.225 10.45C4.92153 10.45 4.63455 10.4797 4.36406 10.5391C4.09358 10.5984 3.82639 10.684 3.5625 10.7958C3.33819 10.8951 3.16337 11.0446 3.03802 11.2442C2.91267 11.4439 2.85 11.6607 2.85 11.8948V12.35ZM9.5 11.4H12.35V9.975H9.5V11.4ZM5.225 9.975C5.55486 9.975 5.83524 9.85955 6.06615 9.62865C6.29705 9.39774 6.4125 9.11736 6.4125 8.7875C6.4125 8.45764 6.29705 8.17726 6.06615 7.94635C5.83524 7.71545 5.55486 7.6 5.225 7.6C4.89514 7.6 4.61476 7.71545 4.38385 7.94635C4.15295 8.17726 4.0375 8.45764 4.0375 8.7875C4.0375 9.11736 4.15295 9.39774 4.38385 9.62865C4.61476 9.85955 4.89514 9.975 5.225 9.975ZM9.5 9.025H12.35V7.6H9.5V9.025ZM7.125 5.225H8.075V1.425H7.125V5.225Z" fill="#9AA1AD"/>
</svg>
}
                  value={designation}
                  onChange={(e) => {
                    setDesignation(e.target.value);
                    if (fieldErrors.designation) setFieldErrors(prev => ({ ...prev, designation: false }));
                  }}
                  className={`light-input rounded-lg hover:border-gray-300 focus:border-[#A91D22] ${fieldErrors.designation ? '!border-red-500' : ''}`}
                />
              </Form.Item>

              <Form.Item label={<span className='text-sm font-medium text-gray-700 mb-0 block'>Email</span>} className='mb-0' layout='vertical'>
                <Input
                disabled
                  size="large"
                  status={fieldErrors.email ? 'error' : undefined}
                  style={{ backgroundColor: "#F8F9FA", borderColor: fieldErrors.email ? "#EF4444" : "#E5E7EB", height: 42 }}
                  placeholder='example@gmail.com'
                  prefix={<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.425 11.4C1.03313 11.4 0.697656 11.2604 0.418594 10.9812C0.139531 10.702 0 10.3663 0 9.97421V1.41946C0 1.02732 0.139531 0.692708 0.418594 0.415625C0.697656 0.138542 1.03313 0 1.425 0H13.775C14.1669 0 14.5023 0.139597 14.7814 0.418791C15.0605 0.697986 15.2 1.03365 15.2 1.42579V9.98054C15.2 10.3727 15.0605 10.7073 14.7814 10.9844C14.5023 11.2615 14.1669 11.4 13.775 11.4H1.425ZM7.6 6.65L1.425 3.10729V9.975H13.775V3.10729L7.6 6.65ZM7.6 4.96771L13.775 1.425H1.425L7.6 4.96771ZM1.425 3.10729V1.425V9.975V3.10729Z" fill="#9AA1AD"/>
</svg>
}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: false }));
                  }}
                  className={`light-input rounded-lg hover:border-gray-300 focus:border-[#A91D22] ${fieldErrors.email ? '!border-red-500' : ''}`}
                />
              </Form.Item>

              <Form.Item label={<span className='text-sm font-medium text-gray-700 mb-0 block'>Password</span>} className='mb-0' layout='vertical'>
                {/* Hidden input to prevent browser from autofilling Designation as username */}
                <input type="text" name="hidden-email" autoComplete="username" value={email} style={{ display: 'none' }} readOnly />
                <Input.Password
                  autoComplete="new-password"
                  size="large"
                  status={fieldErrors.password ? 'error' : undefined}
                  style={{ backgroundColor: "#F8F9FA", borderColor: fieldErrors.password ? "#EF4444" : "#E5E7EB", height: 42 }}
                  className={`light-password rounded-lg hover:border-gray-300 focus:border-[#A91D22] ${fieldErrors.password ? '!border-red-500' : ''}`}
                  placeholder='Create a password'
                  prefix={<svg width="19" height="10" viewBox="0 0 19 10" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4.75 6.65C4.22222 6.65 3.77361 6.46528 3.40417 6.09583C3.03472 5.72639 2.85 5.27778 2.85 4.75C2.85 4.22222 3.03472 3.77361 3.40417 3.40417C3.77361 3.03472 4.22222 2.85 4.75 2.85C5.27778 2.85 5.72639 3.03472 6.09583 3.40417C6.46528 3.77361 6.65 4.22222 6.65 4.75C6.65 5.27778 6.46528 5.72639 6.09583 6.09583C5.72639 6.46528 5.27778 6.65 4.75 6.65ZM4.75 9.5C3.43056 9.5 2.30903 9.03819 1.38542 8.11458C0.461806 7.19097 0 6.06944 0 4.75C0 3.43056 0.461806 2.30903 1.38542 1.38542C2.30903 0.461806 3.43056 0 4.75 0C5.60764 0 6.39931 0.21441 7.125 0.643229C7.85069 1.07205 8.43125 1.64931 8.86667 2.375H15.675L18.05 4.75L14.4875 8.075L12.825 6.8875L11.4 8.075L9.5 6.65H9.10417C8.7875 7.54722 8.22344 8.24653 7.41198 8.74792C6.60052 9.24931 5.71319 9.5 4.75 9.5ZM4.75 8.075C5.58125 8.075 6.31354 7.80781 6.94688 7.27344C7.58021 6.73906 7.94306 6.05625 8.03542 5.225H9.975L11.3604 6.25417L12.7656 5.08646L14.3687 6.23438L15.9917 4.73021L15.0813 3.8H7.93646C7.68576 3.10069 7.27344 2.53003 6.69948 2.08802C6.12552 1.64601 5.47569 1.425 4.75 1.425C3.82639 1.425 3.04132 1.74826 2.39479 2.39479C1.74826 3.04132 1.425 3.82639 1.425 4.75C1.425 5.67361 1.74826 6.45868 2.39479 7.10521C3.04132 7.75174 3.82639 8.075 4.75 8.075Z" fill="#9AA1AD"/>
</svg>
}
                  iconRender={(visible) => (visible ? <EyeTwoTone twoToneColor="#A91D22" /> : <EyeInvisibleOutlined className="text-gray-500" />)}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: false }));
                  }}
                />
              </Form.Item>

              <div className="flex items-center mt-2 mb-1">
                <Checkbox 
                  checked={termsAccepted} 
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (fieldErrors.termsAccepted) setFieldErrors(prev => ({ ...prev, termsAccepted: false }));
                  }}
                  className={`text-sm ${fieldErrors.termsAccepted ? 'text-red-500 font-medium' : 'text-gray-500'}`}
                >
                  I agree to the <a onClick={(e) => { e.preventDefault(); setPrivacyModalOpen(true); }} className="text-[#A91D22] hover:text-[#8a171b] underline cursor-pointer">Privacy Policy</a>
                </Checkbox>
              </div>

              <Button type='primary' size='large' onClick={handleCreateAccount} loading={buttonLoading} className='w-full h-11 mt-2 text-base font-medium !bg-[#A91D22] hover:!bg-[#8a171b] !border-none rounded-lg shadow-md transition-all flex items-center justify-center'>
                Sign Up
              </Button>
            </div>

            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink-0 mx-4 text-gray-400 text-[13px]">Already registered?</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            <Button 
              size='large' 
              onClick={() => navigate("/login")} 
              className='w-full h-11 text-base font-medium !text-[#A91D22] border !border-[#A91D22] !bg-[#fdf2f2] hover:!bg-[#fce4e4] rounded-lg transition-all flex items-center justify-center gap-2'
            >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6.65 13.3V11.875H11.875V1.425H6.65V0H11.875C12.2669 0 12.6023 0.139532 12.8814 0.418594C13.1605 0.697657 13.3 1.03313 13.3 1.425V11.875C13.3 12.2669 13.1605 12.6023 12.8814 12.8814C12.6023 13.1605 12.2669 13.3 11.875 13.3H6.65ZM5.225 9.975L4.21562 8.96562L5.81875 7.3625H0V5.9375H5.81875L4.21562 4.33437L5.225 3.325L8.55 6.65L5.225 9.975Z" fill="#A51C1C"/>
</svg>

              Sign in to your account
            </Button>

            {/* Enterprise registration */}
            <div className="flex items-center mt-5 mb-1">
              <div className="flex-grow border-t border-gray-100"></div>
              {/* <span className="flex-shrink-0 mx-4 text-gray-400 text-[12px]">or</span> */}
              <div className="flex-grow border-t border-gray-100"></div>
            </div>

            {/* <Button
              size='large'
              onClick={() => navigate("/sign-up-enterprise")}
              className='w-full h-11 text-base font-bold !text-white border-none !bg-[#1E293B] hover:!bg-[#2d3748] rounded-xl transition-all flex items-center justify-center gap-2 mt-1 shadow-sm'
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                <path d="M10 22V8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v14" />
                <path d="M2 22V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v18" />
                <path d="M6 6h2" />
                <path d="M6 10h2" />
                <path d="M6 14h2" />
                <path d="M14 10h4" />
                <path d="M14 14h4" />
                <path d="M14 18h4" />
                <path d="M2 22h20" />
              </svg>
              Register as a Client (Enterprise)
            </Button> */}
          </div>
        </div>

        <span className='text-gray-400 text-xs md:text-sm select-none pb-6 mt-auto'>&copy; 2026 Vantage AI. All rights reserved.</span>
      </div>

      {/* Right side: Image Panel */}
      <div className='hidden lg:flex w-1/2 relative bg-black items-center justify-center overflow-hidden'>
        <img src={loginBackground} alt="Vantage AI Dashboard Graphic" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        
        <div className="absolute top-16 left-16 z-10">
          <div className="text-white text-[42px] leading-[1.2] font-bold tracking-tight">
            <div>Turn raw data into</div>
            <div>professional <span className="text-[#FCA5A5]">Dashboards</span></div>
            <div>in minutes.</div>
          </div>
        </div>

        {/* Valiantlytix Logo Overlay */}
        <div className="absolute bottom-12 right-12 z-10">
          <img src={valiantlytixLogo} alt="Valiantlytix Logo" className="w-[180px] h-auto object-contain" />
        </div>
      </div>
      
      <Modal
        title={<span className="text-xl font-bold">Privacy Policy for Valiantlytix Sdn. Bhd.</span>}
        open={isPrivacyModalOpen}
        onCancel={() => setPrivacyModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setPrivacyModalOpen(false)} className="!bg-[#A91D22] hover:!bg-[#8a171b] !border-none">
            Close
          </Button>
        ]}
        width={800}
        centered
      >
        <div className="text-gray-700 text-[14px] space-y-4 max-h-[65vh] overflow-y-auto pr-2 py-2">
          <p><strong>Effective Date:</strong> April 2, 2026 &nbsp;|&nbsp; <strong>Last Updated:</strong> April 2, 2026</p>
          
          <p>At Valiantlytix Sdn. Bhd. ("Valiantlytix", "we", "us", or "our"), we specialize in AI transformation and data analytics. We understand that your data is a valuable asset, and we are committed to protecting your privacy. This policy outlines how we handle your personal data when you visit our website, use our Contact Us form, or subscribe to our communications.</p>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">1. Personal Data We Collect</h3>
          <p>We collect information that identifies you or allows us to contact you, including:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Identity & Contact Data:</strong> Name, job title, company name, email address, and phone number.</li>
            <li><strong>Inquiry Data:</strong> Any specific information or project details you provide in the message field of our contact forms.</li>
            <li><strong>Technical & Usage Data:</strong> IP address, browser type, and interactions with our website (collected via cookies) to optimize your user experience.</li>
          </ul>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">2. Purpose of Data Processing</h3>
          <p>In compliance with the Notice and Choice Principle of the PDPA, we process your data for:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Direct Inquiry Fulfillment:</strong> Responding to your requests for AI solutions, proposals, or training services.</li>
            <li><strong>Relationship Management:</strong> Scheduling discovery calls and managing business interactions.</li>
            <li><strong>Marketing & Engagement:</strong> Sending you newsletters, whitepapers, and updates regarding AI trends and Valiantlytix services.</li>
            <li><strong>Analytics:</strong> Using anonymized data to improve our service offerings and website performance.</li>
          </ul>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">3. Marketing Communications & Newsletters</h3>
          <p>By providing your contact details and opting in, you agree to receive marketing materials from us.</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Personalization:</strong> We may use automated analytics to ensure the insights and newsletters we send are relevant to your industry or interests.</li>
            <li><strong>Opt-out (Unsubscribe):</strong> You have the right to stop receiving marketing emails at any time. Every newsletter or promotional email includes an "Unsubscribe" link. Alternatively, you may contact our DPO to be removed from our mailing list.</li>
          </ul>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">4. Consent</h3>
          <p>By submitting your information through our website, you explicitly consent to the processing of your personal data as described.</p>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">5. Disclosure of Your Data</h3>
          <p>We do not sell, rent, or trade your personal data. We only share information with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Authorized Personnel:</strong> Employees within Valiantlytix who require the data to assist you.</li>
            <li><strong>Trusted Data Processors & Affiliates:</strong> Third-party providers and affiliates (such as CRM platforms or cloud storage) used to support AI-driven processing and core site functionalities. All providers are contractually bound to maintain strict confidentiality.</li>
            <li><strong>Legal Compliance:</strong> Regulatory bodies or law enforcement when required by Malaysian law.</li>
          </ul>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">6. Data Security & Integrity</h3>
          <p>As an AI-driven organization, we employ high-standard technical and organizational measures to protect your data from unauthorized access, alteration, or disclosure. We utilize encryption and secure access protocols to maintain data integrity.</p>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">7. Data Retention</h3>
          <p>We retain your personal data only as long as necessary to fulfill the purposes outlined in this policy or to meet legal and regulatory requirements. Once no longer needed, data is securely deleted or permanently anonymized.</p>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">8. Your Rights under PDPA 2024</h3>
          <p>You have the following rights regarding your personal data:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Access & Correction:</strong> The right to request a copy of your data or correct any inaccuracies.</li>
            <li><strong>Withdrawal of Consent:</strong> The right to stop us from processing your data for marketing or other purposes.</li>
            <li><strong>Data Portability:</strong> The right to request your data in a structured, machine-readable format.</li>
            <li><strong>Automated Decision-Making:</strong> The right to request human intervention regarding any automated profiling that significantly affects you.</li>
          </ul>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">9. User-Generated Content License</h3>
          <p>By submitting content to our site (such as comments, reviews, or images), you grant Valiantlytix a non-exclusive, royalty-free, worldwide license to use, modify, and distribute that content to operate, promote, and improve our services.</p>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">10. Data Protection Officer (DPO)</h3>
          <p>In accordance with the Personal Data Protection (Amendment) Act 2024, we have appointed a Data Protection Officer. For any questions, data access requests, or complaints, please contact:</p>
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg mt-3 text-sm">
            <p className="mb-1"><strong>Attn:</strong> Data Protection Officer</p>
            <p className="mb-1"><strong>Valiantlytix Sdn. Bhd.</strong></p>
            <p className="mb-1"><strong>Email:</strong> legal@valiantlytix.com</p>
            <p className="mb-0"><strong>Location:</strong> Petaling Jaya, Selangor, Malaysia</p>
          </div>

          <h3 className="font-bold text-[16px] text-gray-900 mt-5">11. Amendments</h3>
          <p>We reserve the right to update this policy to reflect changes in our business practices or Malaysian law. Updates will be posted on this page with a revised "Last Updated" date.</p>
        </div>
      </Modal>

      {/* Error Modal */}
      <AuthErrorModal
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        title={errorTitle}
        message={errorMessage || ""}
      />
    </div>
  )
}

export default ClentPrefilledSignUpPage;